<template>
  <div>
    <app-nav />
    <div class="activity-board">
      <spinner class="animated" v-if="isLoading"></spinner>
      <div class="board-title">{{ app.name }}</div>
      <div class="board-summary">
        <div class="summary-item">
          <h2 class="count">{{ getSummaryData('previous', 'activeUsers') }}</h2>
          <h5 class="text">{{ $t('activities.info.mau_previous_month') }}</h5>
        </div>
        <div class="summary-item">
          <h2 class="count">{{ getSummaryData('current', 'activeUsers') }}</h2>
          <h5 class="text">{{ $t('activities.info.mau_current_month') }}</h5>
        </div>
        <div class="summary-item">
          <h2 class="count">{{ getSummaryData('previous', 'installs') }}</h2>
          <h5 class="text">{{ $t('activities.info.sdk_previous_month') }}</h5>
        </div>
        <div class="summary-item">
          <h2 class="count">{{ getSummaryData('current', 'installs') }}</h2>
          <h5 class="text">{{ $t('activities.info.sdk_current_month') }}</h5>
        </div>
      </div>

      <BarChart
        class="chart"
        :chart-header="$t('activities.info.mau_per_month')"
        :chart-footer="$t('activities.info.month')"
        :height="barChart.HEIGHT"
        :width="barChart.WIDTH"
        :data-set="activities.monthlyActiveUsers"
        :data-fields="dataFields"
        :margin-left="barChart.MARGIN_LEFT"
        :margin-top="barChart.MARGIN_TOP"
        :tick-count="barChart.TICK_COUNT"
        :bar-padding="barChart.PADDING"
        :bar-color="barChart.BLUE_COLOR"
        :bar-hover-color="barChart.BLUE_HOVER_COLOR"
        :bar-current-color="barChart.CURRENT_COLOR"
        :bar-current-hover-color="barChart.CURRENT_HOVER_COLOR"
        :render-type="barChart.RENDER_TYPE_MONTHLY"
      />

      <BarChart
        class="chart"
        :chart-header="$t('activities.info.dau_last_90days')"
        :chart-footer="$t('activities.info.day')"
        :height="barChart.HEIGHT"
        :width="barChart.WIDTH"
        :data-set="activities.dailyActiveUsers"
        :data-fields="dataFields"
        :margin-left="barChart.MARGIN_LEFT"
        :margin-top="barChart.MARGIN_TOP"
        :tick-count="barChart.TICK_COUNT"
        :bar-padding="barChart.PADDING"
        :bar-color="barChart.BLUE_COLOR"
        :bar-hover-color="barChart.BLUE_HOVER_COLOR"
        :bar-current-color="barChart.CURRENT_COLOR"
        :bar-current-hover-color="barChart.CURRENT_HOVER_COLOR"
        :render-type="barChart.RENDER_TYPE_DAILY"
      />

      <BarChart
        class="chart"
        :chart-header="$t('activities.info.sdk_install_per_month')"
        :chart-footer="$t('activities.info.month')"
        :data-set="activities.monthlySdkInstalls"
        :data-fields="dataFields"
        :height="barChart.HEIGHT"
        :width="barChart.WIDTH"
        :margin-left="barChart.MARGIN_LEFT"
        :margin-top="barChart.MARGIN_TOP"
        :tick-count="barChart.TICK_COUNT"
        :bar-padding="barChart.PADDING"
        :bar-color="barChart.ORANGE_COLOR"
        :bar-hover-color="barChart.ORANGE_HOVER_COLOR"
        :bar-current-color="barChart.CURRENT_COLOR"
        :bar-current-hover-color="barChart.CURRENT_HOVER_COLOR"
        :render-type="barChart.RENDER_TYPE_MONTHLY"
      />

      <BarChart
        class="chart"
        :chart-header="$t('activities.info.sdk_install_last_90days')"
        :chart-footer="$t('activities.info.day')"
        :data-set="activities.dailySdkInstalls"
        :data-fields="dataFields"
        :height="barChart.HEIGHT"
        :width="barChart.WIDTH"
        :margin-left="barChart.MARGIN_LEFT"
        :margin-top="barChart.MARGIN_TOP"
        :tick-count="barChart.TICK_COUNT"
        :bar-padding="barChart.PADDING"
        :bar-color="barChart.ORANGE_COLOR"
        :bar-hover-color="barChart.ORANGE_HOVER_COLOR"
        :bar-current-color="barChart.CURRENT_COLOR"
        :bar-current-hover-color="barChart.CURRENT_HOVER_COLOR"
        :render-type="barChart.RENDER_TYPE_DAILY"
      />
    </div>
  </div>
</template>

<script>
import * as _ from 'lodash';
import debounce from 'lodash/debounce';
import * as moment from 'moment';
import Vue from 'vue';
import { mapGetters } from 'vuex';

import BarChart from './modules/BarChart.vue';
import AppNav from '../common/modules/AppNav.vue';
import { Spinner } from 'components/loaders';
import { getActivity } from 'store/queries/activity';

const BAR_CHART_MARGIN = 60;

export default {
  name: 'ActivitiesBoard',
  data() {
    return {
      isLoading: false,
      activities: {
        monthlyActiveUsers: [],
        monthlySdkInstalls: [],
        dailyActiveUsers: [],
        dailySdkInstalls: [],
      },
      dataFields: ['date', 'count'],
      barChart: {
        HEIGHT: 200,
        WIDTH: this.decideBarChartWidth(),
        MARGIN_LEFT: BAR_CHART_MARGIN,
        MARGIN_TOP: 40,
        TICK_COUNT: 5,
        PADDING: 0.5,
        RENDER_TYPE_DAILY: 'daily',
        RENDER_TYPE_MONTHLY: 'monthly',
        AXIS_COLOR: '#888',
        CURRENT_COLOR: 'rgba(249, 203, 0, 0.8)',
        CURRENT_HOVER_COLOR: 'rgba(249, 203, 0, 1)',
        BLUE_COLOR: 'rgba(70, 182, 176, 0.8)',
        BLUE_HOVER_COLOR: 'rgba(70, 182, 176, 1)',
        ORANGE_COLOR: 'rgba(232, 132, 63, 0.8)',
        ORANGE_HOVER_COLOR: 'rgba(232, 132, 63, 1)',
      },
    };
  },
  created() {
    window.addEventListener('resize', this.onWindowResize);
  },
  mounted() {
    this.load();
  },
  destroyed() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  components: {
    BarChart,
    AppNav,
    Spinner,
  },
  computed: {
    ...mapGetters('application', ['app']),
  },
  methods: {
    load() {
      this.isLoading = true;
      getActivity(this.$route.params.appId).then(response => {
        this.activities.monthlyActiveUsers = _.get(
          response,
          'body.data.app.activity.active_users.monthly',
          []
        );
        this.activities.monthlySdkInstalls = _.get(
          response,
          'body.data.app.activity.installs.monthly',
          []
        );
        this.activities.dailyActiveUsers = _.get(
          response,
          'body.data.app.activity.active_users.daily',
          []
        );
        this.activities.dailySdkInstalls = _.get(
          response,
          'body.data.app.activity.installs.daily',
          []
        );
        this.isLoading = false;
      });
    },

    getActivityData(dataType, renderType) {
      return _.get(this.fakeActivitityData, `${dataType}.${renderType}`);
    },
    /**
     * Method to get chart summary data
     * @param(month) - string value as current or previous
     * @param(type) - string value like installs or activeUsers
     * @return - current/previous month's sdk install/active user's count
     */
    getSummaryData(month, type) {
      // return current month date in the format ' YYYY-MM-DD' e.g 2020-09-01
      let selectedMonth = moment()
        .startOf('month')
        .format('YYYY-MM-DD');
      if (month === 'previous') {
        // return previous month date in the format ' YYYY-MM-DD' e.g 2020-08-01
        selectedMonth = moment()
          .startOf('month')
          .subtract(1, 'months')
          .format('YYYY-MM-DD');
      }

      // Helper method to convert any date format from the backend ino YYYY-MM-DD
      const format = date => moment(date).format('YYYY-MM-DD');

      let matchedResult = this.activities.monthlyActiveUsers.filter(
        data => format(data.date) === selectedMonth
      );

      if (type === 'installs') {
        matchedResult = this.activities.monthlySdkInstalls.filter(
          data => format(data.date) === selectedMonth
        );
      }

      return this.formatNumber(_.get(matchedResult, '[0].count', 0));
    },
    formatNumber(number) {
      return new Intl.NumberFormat().format(number);
    },
    decideBarChartWidth() {
      return window.innerWidth - 2 * BAR_CHART_MARGIN;
    },
    onWindowResize: debounce(
      function() {
        this.barChart.WIDTH = this.decideBarChartWidth();
      },
      200,
      { leading: false, trailing: true }
    ),
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
</style>
